import { useMemo } from 'react'
import { usePartnerId } from '../../../utils/routing/params.hooks'
import { useCurrentAccountContext } from '../../../utils/current-account/CurrentAccountContext'
import { FeatureToggle } from '../../../legacy/contexts/featureToggles/FeatureToggleContext'
import clientEnv from '../../../utils/global/clientEnv'

/**
 * Checks if we should can display the partner write access marker in the UI
 * Only displayed for internal users and when the feature toggle is enabled
 */
export const useIsPartnerWriteAccessRequired = () => {
  const { account } = useCurrentAccountContext()
  const partnerId = usePartnerId({ throwOnMissing: false })

  return Boolean(
    // We only show this in production since users can make updates on local/stage/enterprise-dev
    clientEnv.PUBLIC_CLIENT_ENVIRONMENT === 'production' &&
      partnerId != null &&
      account?.isInternal &&
      account?.featureToggles?.find(
        feature => feature?.name === FeatureToggle.EnforcePerPartnerWriteAccess
      )
  )
}

/**
 * Checks if the current user has write access for the current
 * selected partner. This is only required when the user is internal
 *
 * Assumes the user is already assigned to the current partner
 */
export const useHasWriteAccessForCurrentPartner = () => {
  const partnerId = usePartnerId({ throwOnMissing: false })
  const isPartnerWriteAccessRequired = useIsPartnerWriteAccessRequired()
  const { account } = useCurrentAccountContext()

  const hasPartnerWriteAccess = useMemo(() => {
    if (!isPartnerWriteAccessRequired) {
      return true
    }

    if (account?.hasAllPartnersWriteAccess) {
      return true
    }

    return Boolean(
      account?.partnerWriteAccess?.some(writeAccess => writeAccess?.partnerId === partnerId)
    )
  }, [account, partnerId, isPartnerWriteAccessRequired])

  return hasPartnerWriteAccess
}
